import { IInspectionAdapter } from './IInspectionAdapter';
import { ApiResponse } from '../../types/api';
import {
  Inspection,
  Verification,
  Closure,
  ClosureEvent,
  CreateInspectionDto,
  CreateVerificationDto,
  InspectionFilter,
  VerificationFilter,
} from '../../types/inspection';
import { WorkOrder } from '../../types/workOrder';
import { hasApiEndpoint } from '../../config/backend';

const MOCK_INSPECTIONS: Inspection[] = [
  {
    inspection_id: 'INS-2026-00001',
    work_order_id: 'WO-2026-00046',
    complaint_id: 'CMP-2026-00102',
    inspector_id: 'USR-00004',
    inspector_name: 'Suresh',
    inspection_date: '2026-09-13T14:00:00Z',
    result: 'pass',
    checklist: [
      { item_id: 'CHK-001', label: 'Work completed as per scope', checked: true },
      { item_id: 'CHK-002', label: 'Area cleaned after work', checked: true },
      { item_id: 'CHK-003', label: 'All tools removed', checked: true },
      { item_id: 'CHK-004', label: 'System tested and operational', checked: true },
      { item_id: 'CHK-005', label: 'Client walkthrough completed', checked: true },
    ],
    notes: 'Plumbing repair completed successfully. No leaks detected under pressure test.',
    photo_urls: [],
    defects: [],
    created_at: '2026-09-13T14:00:00Z',
  },
];

const MOCK_VERIFICATIONS: Verification[] = [
  {
    verification_id: 'VER-2026-00001',
    inspection_id: 'INS-2026-00001',
    complaint_id: 'CMP-2026-00102',
    work_order_id: 'WO-2026-00046',
    verifier_id: 'USR-00007',
    verifier_name: 'Dept User',
    verification_date: '2026-09-13T15:30:00Z',
    is_satisfactory: true,
    rating: 4,
    comments: 'Work looks good. Washroom is dry and functional.',
    created_at: '2026-09-13T15:30:00Z',
  },
];

const MOCK_CLOSURES: Closure[] = [
  {
    closure_id: 'CLS-2026-00001',
    complaint_id: 'CMP-2026-00102',
    work_order_id: 'WO-2026-00046',
    verification_id: 'VER-2026-00001',
    closed_by_user_id: 'USR-00007',
    closed_by_user_name: 'Dept User',
    closed_at: '2026-09-13T16:00:00Z',
    final_status: 'closed',
    archive_eligible: true,
    total_duration_hours: 72,
    satisfaction_rating: 4,
  },
];

const MOCK_CLOSURE_EVENTS: ClosureEvent[] = [
  {
    event_id: 'CLE-2026-00001',
    event_type: 'inspection',
    entity_id: 'INS-2026-00001',
    description: 'Inspection completed — Pass',
    performed_by: 'Suresh',
    performed_at: '2026-09-13T14:00:00Z',
  },
  {
    event_id: 'CLE-2026-00002',
    event_type: 'verification',
    entity_id: 'VER-2026-00001',
    description: 'Department verification — Satisfactory (4/5)',
    performed_by: 'Dept User',
    performed_at: '2026-09-13T15:30:00Z',
  },
  {
    event_id: 'CLE-2026-00003',
    event_type: 'closure',
    entity_id: 'CLS-2026-00001',
    description: 'Complaint formally closed',
    performed_by: 'Dept User',
    performed_at: '2026-09-13T16:00:00Z',
  },
];

const MOCK_COMPLETED_WO: WorkOrder = {
  work_order_id: 'WO-2026-00046',
  complaint_id: 'CMP-2026-00102',
  complaint_title: 'Main Pipe Leakage in Washroom Block B',
  vendor_id: 'VND-002',
  vendor_name: 'Alpha Plumbing & Piping',
  supervisor_name: 'Supervisor Suresh',
  technician_name: 'Tech Ali',
  work_category_name: 'Plumbing & Drainage',
  description: 'Replace cracked 2-inch PVC junction pipe under sink',
  scope_of_work: 'Isolate main valve, replace PVC elbow junction, install new seal ring, and test line under 5 bar pressure.',
  estimated_cost: 180.00,
  start_date: '2026-09-13T09:00:00Z',
  target_date: '2026-09-13T12:00:00Z',
  completion_date: '2026-09-13T11:30:00Z',
  status: 'Completed',
  created_at: '2026-09-12T11:45:00Z',
  attachment_urls: [],
};

export class GASInspectionAdapter implements IInspectionAdapter {
  private inspections: Inspection[] = [...MOCK_INSPECTIONS];
  private verifications: Verification[] = [...MOCK_VERIFICATIONS];
  private closures: Closure[] = [...MOCK_CLOSURES];
  private closureEvents: ClosureEvent[] = [...MOCK_CLOSURE_EVENTS];

  async getInspections(filter?: InspectionFilter): Promise<ApiResponse<Inspection[]>> {
    if (!hasApiEndpoint()) {
      let data = [...this.inspections];
      if (filter?.work_order_id) data = data.filter(i => i.work_order_id === filter.work_order_id);
      if (filter?.complaint_id) data = data.filter(i => i.complaint_id === filter.complaint_id);
      if (filter?.result) data = data.filter(i => i.result === filter.result);
      return { success: true, data, message: 'Inspections loaded' };
    }
    return { success: false, data: [], message: 'API not configured' };
  }

  async getInspectionById(id: string): Promise<ApiResponse<Inspection>> {
    if (!hasApiEndpoint()) {
      const ins = this.inspections.find(i => i.inspection_id === id);
      if (ins) return { success: true, data: ins, message: 'Inspection loaded' };
      return { success: false, data: null as any, message: 'Inspection not found' };
    }
    return { success: false, data: null as any, message: 'API not configured' };
  }

  async createInspection(data: CreateInspectionDto): Promise<ApiResponse<Inspection>> {
    if (!hasApiEndpoint()) {
      const newInspection: Inspection = {
        inspection_id: `INS-2026-${String(this.inspections.length + 2).padStart(5, '0')}`,
        work_order_id: data.work_order_id,
        complaint_id: data.complaint_id,
        inspector_id: 'USR-00004',
        inspector_name: 'Suresh',
        inspection_date: new Date().toISOString(),
        result: data.result,
        checklist: data.checklist,
        notes: data.notes,
        photo_urls: data.photo_urls,
        defects: data.defects,
        created_at: new Date().toISOString(),
      };
      this.inspections.unshift(newInspection);

      this.closureEvents.unshift({
        event_id: `CLE-2026-${String(this.closureEvents.length + 1).padStart(5, '0')}`,
        event_type: 'inspection',
        entity_id: newInspection.inspection_id,
        description: `Inspection completed — ${data.result.charAt(0).toUpperCase() + data.result.slice(1)}`,
        performed_by: 'Suresh',
        performed_at: newInspection.inspection_date,
      });

      return { success: true, data: newInspection, message: 'Inspection submitted' };
    }
    return { success: false, data: null as any, message: 'API not configured' };
  }

  async getVerifications(filter?: VerificationFilter): Promise<ApiResponse<Verification[]>> {
    if (!hasApiEndpoint()) {
      let data = [...this.verifications];
      if (filter?.complaint_id) data = data.filter(v => v.complaint_id === filter.complaint_id);
      if (filter?.work_order_id) data = data.filter(v => v.work_order_id === filter.work_order_id);
      return { success: true, data, message: 'Verifications loaded' };
    }
    return { success: false, data: [], message: 'API not configured' };
  }

  async createVerification(data: CreateVerificationDto): Promise<ApiResponse<Verification>> {
    if (!hasApiEndpoint()) {
      const newVerification: Verification = {
        verification_id: `VER-2026-${String(this.verifications.length + 1).padStart(5, '0')}`,
        inspection_id: data.inspection_id,
        complaint_id: data.complaint_id,
        work_order_id: data.work_order_id,
        verifier_id: 'USR-00007',
        verifier_name: 'Dept User',
        verification_date: new Date().toISOString(),
        is_satisfactory: data.is_satisfactory,
        rating: data.rating,
        comments: data.comments,
        created_at: new Date().toISOString(),
      };
      this.verifications.unshift(newVerification);

      this.closureEvents.unshift({
        event_id: `CLE-2026-${String(this.closureEvents.length + 1).padStart(5, '0')}`,
        event_type: 'verification',
        entity_id: newVerification.verification_id,
        description: `Department verification — ${data.is_satisfactory ? 'Satisfactory' : 'Unsatisfactory'} (${data.rating}/5)`,
        performed_by: 'Dept User',
        performed_at: newVerification.verification_date,
      });

      return { success: true, data: newVerification, message: 'Verification submitted' };
    }
    return { success: false, data: null as any, message: 'API not configured' };
  }

  async closeComplaint(complaintId: string, data: { verification_id: string; satisfaction_rating: number }): Promise<ApiResponse<Closure>> {
    if (!hasApiEndpoint()) {
      const newClosure: Closure = {
        closure_id: `CLS-2026-${String(this.closures.length + 1).padStart(5, '0')}`,
        complaint_id: complaintId,
        work_order_id: MOCK_COMPLETED_WO.work_order_id,
        verification_id: data.verification_id,
        closed_by_user_id: 'USR-00007',
        closed_by_user_name: 'Dept User',
        closed_at: new Date().toISOString(),
        final_status: 'closed',
        archive_eligible: true,
        total_duration_hours: 72,
        satisfaction_rating: data.satisfaction_rating,
      };
      this.closures.unshift(newClosure);

      this.closureEvents.unshift({
        event_id: `CLE-2026-${String(this.closureEvents.length + 1).padStart(5, '0')}`,
        event_type: 'closure',
        entity_id: newClosure.closure_id,
        description: 'Complaint formally closed',
        performed_by: 'Dept User',
        performed_at: newClosure.closed_at,
      });

      return { success: true, data: newClosure, message: 'Complaint closed successfully' };
    }
    return { success: false, data: null as any, message: 'API not configured' };
  }

  async getClosureHistory(complaintId: string): Promise<ApiResponse<ClosureEvent[]>> {
    if (!hasApiEndpoint()) {
      const events = this.closureEvents.filter(e => {
        const ins = this.inspections.find(i => i.inspection_id === e.entity_id && i.complaint_id === complaintId);
        const ver = this.verifications.find(v => v.verification_id === e.entity_id && v.complaint_id === complaintId);
        const clo = this.closures.find(c => c.closure_id === e.entity_id && c.complaint_id === complaintId);
        return ins || ver || clo;
      });
      return { success: true, data: events.length > 0 ? events : this.closureEvents, message: 'Closure history loaded' };
    }
    return { success: false, data: [], message: 'API not configured' };
  }
}
